/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/evidence/EvidenceRow.tsx
 * FILE_ID: QUOTE-CLAIMS-EVIDENCE-ROW-001
 * PURPOSE: Render and edit one ordered evidence-manifest row.
 */

import { useEffect, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  FileQuestion,
  FileText,
  Mail,
  Trash2,
} from 'lucide-react'

import type { ClaimEvidence, EvidenceUpdate } from './types'

interface EvidenceRowProps {
  item: ClaimEvidence
  index: number
  total: number
  editable: boolean
  disabled: boolean
  onUpdate: (values: EvidenceUpdate) => Promise<void>
  onRemove: () => Promise<void>
  onMove: (direction: -1 | 1) => Promise<void>
  lines: Array<{ id: string; description: string }>
}

function formatSize(size: number | null): string {
  if (!size) return ''
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function EvidenceRow({
  item,
  index,
  total,
  editable,
  disabled,
  onUpdate,
  onRemove,
  onMove,
  lines,
}: EvidenceRowProps) {
  const [caption, setCaption] = useState(item.caption)

  useEffect(() => setCaption(item.caption), [item.caption])

  const label = item.file_name || item.email_subject || 'Source unavailable'
  const secondary = item.source_type === 'email'
    ? 'Email evidence'
    : [item.file_content_type, formatSize(item.file_size)]
      .filter(Boolean)
      .join(' - ')

  return (
    <li className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-slate-400">
          {item.is_missing
            ? <FileQuestion className="h-5 w-5 text-rose-500" />
            : item.source_type === 'email'
              ? <Mail className="h-5 w-5" />
              : <FileText className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
            {label}
          </p>
          <p className="truncate text-xs text-slate-500">
            {item.is_missing ? 'The original source was removed' : secondary}
          </p>
          <input
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            onBlur={() => {
              if (caption !== item.caption) {
                void onUpdate({ caption }).catch(() => undefined)
              }
            }}
            disabled={!editable || disabled}
            aria-label={`Caption for ${label}`}
            placeholder="Add a caption"
            className="mt-2 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs outline-none focus:border-amber-500 disabled:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:disabled:bg-slate-800"
          />
        </div>
        {editable && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              title="Move up"
              aria-label={`Move ${label} up`}
              disabled={disabled || index === 0}
              onClick={() => void onMove(-1).catch(() => undefined)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-700"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Move down"
              aria-label={`Move ${label} down`}
              disabled={disabled || index === total - 1}
              onClick={() => void onMove(1).catch(() => undefined)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-700"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Remove evidence"
              aria-label={`Remove ${label}`}
              disabled={disabled}
              onClick={() => void onRemove().catch(() => undefined)}
              className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 pl-8 text-xs text-slate-600 dark:text-slate-300">
        {lines.length > 0 && <label className="flex items-center gap-2">Claim line<select value={item.claim_line ?? ''} disabled={!editable || disabled} onChange={(event) => void onUpdate({ claim_line_id: event.target.value || null }).catch(() => undefined)} className="min-h-8 max-w-56 rounded-md border px-2 dark:bg-slate-900"><option value="">Whole claim</option>{lines.map((line) => <option key={line.id} value={line.id}>{line.description}</option>)}</select></label>}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={item.include_in_document}
            disabled={!editable || disabled || item.is_missing}
            onChange={(event) => void onUpdate({
              include_in_document: event.target.checked,
            }).catch(() => undefined)}
          />
          Include in document
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={item.include_on_send}
            disabled={!editable || disabled || item.is_missing}
            onChange={(event) => void onUpdate({
              include_on_send: event.target.checked,
            }).catch(() => undefined)}
          />
          Attach when sending
        </label>
      </div>
    </li>
  )
}
